import { instance as axios } from './baseUrl';

// API du parcours Santé Mobilité v2.
// Pattern moderne (cf. rcApi.js) : POST /subscription/sante-mobilite/...

export interface CountryDto {
  id: string | number;
  name: string;
  zone?: string | number;
  lat?: number;
  lng?: number;
}

const toISODateTime = (dateStr: string | null): string | null => {
  if (!dateStr) return null;
  if (dateStr.includes('T')) return dateStr;
  return `${dateStr}T00:00:00.000Z`;
};

/**
 * Détecte la réponse 403 spéciale "pays non couvert" (zone 0).
 * Le back renvoie `{ message, code: 'COUNTRY_NOT_COVERED' }` avec ce code.
 */
export const isCountryNotCoveredError = (error: unknown): boolean => {
  const e = error as { response?: { status?: number; data?: { code?: string } } };
  return (
    e?.response?.status === 403 &&
    e?.response?.data?.code === 'COUNTRY_NOT_COVERED'
  );
};

// Liste des pays (avec lat/lng + zone) — endpoint public.
export const listCountriesApi = () => axios.get('/countries');

// Prix santé mobilité pour une période + destination donnée.
// TODO BACK: aligner sur le pattern POST /subscription/sante-mobilite/price
// si pas encore en place (aujourd'hui legacy GET /payments/price/health_mobility).
//
// Note métier :
// - `hasFrenchSocialSecurity` est transmis pour que le back puisse forcer le
//   tarif zone 4 ("étranger") quand l'étudiant n'a pas de num de sécu français,
//   peu importe la zone du pays sélectionné. La règle est portée côté back pour
//   éviter qu'un client malicieux ne contourne le tarif zone 4 en envoyant
//   directement la zone du pays cible.
// - `schoolName` est transmis pour les utilisateurs en fast register (pas
//   encore inscrits) afin que le back applique le `partnerLevelHealthMobility`
//   négocié par l'école détectée via le slug `?school=...` dans l'URL d'entrée.
//   Sans ce param le back retombe sur `partnerLevelHealthMobilityPrice` (legacy,
//   utilisé par les users déjà connectés) ou sur le tarif public.
export interface PricePayload {
  zone: string | number;
  monthsCount: number;
  partnerLevelHealthMobilityPrice?: number;
  hasFrenchSocialSecurity?: boolean | null;
  schoolName?: string | null;
}

export const priceSanteMobiliteApi = ({
  zone,
  monthsCount,
  partnerLevelHealthMobilityPrice = 0,
  hasFrenchSocialSecurity,
  schoolName,
}: PricePayload) => {
  const params = new URLSearchParams({
    zone: `${zone}`,
    month: `${monthsCount}`,
    partnerLevelHealthMobilityPrice: `${partnerLevelHealthMobilityPrice}`,
  });
  if (typeof hasFrenchSocialSecurity === 'boolean') {
    params.set('hasFrenchSocialSecurity', `${hasFrenchSocialSecurity}`);
  }
  if (schoolName && schoolName.trim()) {
    params.set('schoolName', schoolName.trim());
  }
  return axios.get(`/payments/price/health_mobility?${params.toString()}`);
};

// Téléchargement attestation (parcours inclusion gratuit, pas de paiement).
export const downloadAttestationApi = (params: {
  startDate: string;
  endDate: string;
  country: CountryDto;
}) =>
  axios.post(
    '/contracts/health_mobility',
    {
      startDate: params.startDate,
      endDate: params.endDate,
      country: params.country,
      sessionStripe: '',
    },
    { responseType: 'blob' },
  );

// TODO BACK: endpoint à créer (statut email pour le fast register).
export const checkEmailStatusApi = (email: string) =>
  axios.get(`/students/check-email-status?email=${encodeURIComponent(email)}`);

// Souscription parcours payant — crée un PaymentIntent côté back et renvoie
// son clientSecret (+ publicKey du compte Stripe Santé Mobilité, distinct
// de celui de Home/RC).
//
// Réponse attendue :
//   {
//     clientSecret: "pi_xxx_secret_yyy",
//     publicKey: "pk_xxx",
//     contractReference: "StudHM-...",
//     stripeConfigType: "payment_intent",
//     subscriptionId?: string
//   }
export const subscriptionSanteMobiliteApi = ({
  startDate,
  endDate,
  country,
  contractReference,
  hasFrenchSocialSecurity,
}: {
  startDate: string;
  endDate: string;
  country: CountryDto;
  contractReference?: string | null;
  hasFrenchSocialSecurity?: boolean | null;
}) =>
  axios.post('/subscription/sante-mobilite/session', {
    startDate: toISODateTime(startDate),
    endDate: toISODateTime(endDate),
    country,
    contractReference,
    // Le back applique la règle métier zone 4 forcée si false, et peut décider
    // de persister ce booléen sur le student / contrat pour traçabilité.
    hasFrenchSocialSecurity,
  });

// Note design Santé Mobilité v2 : à la différence de RC/Habitation, l'attestation
// PDF est générée + envoyée par mail directement par le webhook Stripe côté back
// (sans intervention front). L'utilisateur reçoit donc le PDF par mail dans tous
// les cas, mais on lui permet aussi de le DL direct depuis /success via cet
// endpoint (UX mobile-friendly : pas besoin de switcher d'app pour récupérer
// le PDF). On poll 409 (ATTESTATION_PENDING) tant que le webhook n'a pas
// terminé sa génération.
//
// Codes de retour back :
//   200 → { attestationId, contractReference, fileName } : on DL via /files/document/{id}
//   409 → { code: "ATTESTATION_PENDING" } : webhook en cours, retry ~1.5s
//   404 → contrat introuvable
//   403 → contrat appartient à un autre utilisateur
//   401 → non authentifié
export const attestationInfoSanteMobiliteApi = (contractReference: string) =>
  axios.get(
    `/subscription/sante-mobilite/attestation-info/${encodeURIComponent(contractReference)}`,
  );

// Récupération du lien de signature SellAndSign après paiement Stripe réussi.
// À n'appeler que si /session a retourné requiresSignature: true (zones 1/2/3).
//
// Codes de retour back :
//   200 → { signatureUrl, contractReference } : on redirige l'utilisateur
//   404 → contrat introuvable
//   409 → contrat trouvé mais signature pas encore générée (webhook en cours)
//         → retry conseillé après ~2s
//   403 → l'utilisateur n'est pas le propriétaire du contrat
export const signatureLinkSanteMobiliteApi = (contractReference: string) =>
  axios.get(
    `/subscription/sante-mobilite/signature-link/${encodeURIComponent(contractReference)}`,
  );
